import ConsoleLocalStorage from "../storage/storage";
import { APP_MANAGEMENT_URL } from "../config";

/**
 * Fetches an image with authentication and returns a blob URL
 * @param {string} imageUrl - The proxy URL for the image
 * @returns {Promise<string|null>} - Blob URL for the image or null on error
 */
export async function fetchAuthenticatedImage(imageUrl) {
  if (!imageUrl) return null;

  try {
    const token = ConsoleLocalStorage.getAccessToken();
    if (!token) {
      console.warn("No access token available for authenticated image fetch");
      return null;
    }

    const headers = {
      Authorization: `Bearer ${token}`,
    };
    // The organization media proxy additionally requires the org context header.
    if (imageUrl.includes("/organization/") && imageUrl.includes("/media/proxy")) {
      const orgId = ConsoleLocalStorage.getOrganization()?.id;
      if (orgId) {
        headers["x-organization-id"] = orgId;
      }
    }

    const response = await fetch(imageUrl, { headers });

    if (!response.ok) {
      throw new Error(`Failed to fetch image: ${response.status}`);
    }

    const blob = await response.blob();
    return URL.createObjectURL(blob);
  } catch (error) {
    console.error("Error fetching authenticated image:", error);
    return null;
  }
}

/**
 * Downloads a document with authentication
 * @param {string} filename - The document filename
 * @param {string} container - Container name (e.g., 'dc-documents')
 * @returns {Promise<void>}
 */
export async function downloadAuthenticatedDocument(
  filename,
  container = "dc-documents"
) {
  const url = `${APP_MANAGEMENT_URL}cdn/${container}/${filename}`;

  try {
    const token = ConsoleLocalStorage.getAccessToken();
    if (!token) {
      throw new Error("No access token available");
    }

    const response = await fetch(url, {
      headers: {
        Authorization: `Bearer ${token}`,
      },
    });

    if (!response.ok) {
      throw new Error(`Failed to download document: ${response.status}`);
    }

    const blob = await response.blob();
    const blobUrl = URL.createObjectURL(blob);

    // Create download link and trigger download
    const link = document.createElement("a");
    link.href = blobUrl;
    link.download = filename;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);

    // Clean up blob URL after a short delay
    setTimeout(() => URL.revokeObjectURL(blobUrl), 100);
  } catch (error) {
    console.error("Error downloading document:", error);
    throw error;
  }
}

/**
 * Builds the authenticated organization media proxy URL for a stored banner/logo.
 * Accepts either the bare blob path (new records) or a legacy full blob URL
 * (the backend normalises both). Returns "" when there is nothing to load.
 * @param {string} pathOrUrl - Value stored in org.banner / org.logo
 * @returns {string}
 */
export function buildOrgMediaUrl(pathOrUrl) {
  if (!pathOrUrl) return "";
  const orgId = ConsoleLocalStorage.getOrganization()?.id;
  if (!orgId) return "";
  // The proxy accepts only a flat blob name (it rejects "/", "\", ".."). New
  // records store the bare path already; legacy records store a full blob URL,
  // so reduce either form to just the file name (drop any path segments/query).
  const blobName = pathOrUrl.split("?")[0].split("/").pop();
  if (!blobName) return "";
  return `${APP_MANAGEMENT_URL}organization/${orgId}/media/proxy?path=${encodeURIComponent(
    blobName
  )}`;
}

/**
 * Revokes a blob URL to free memory
 * @param {string} blobUrl - The blob URL to revoke
 */
export function revokeImageUrl(blobUrl) {
  if (blobUrl && blobUrl.startsWith("blob:")) {
    URL.revokeObjectURL(blobUrl);
  }
}

